'use client';

import { type ChangeEvent } from 'react';
import { Box, Grid, GridItem, HStack, VStack } from '@terra/ui-styled-system';
import {
  Tick01Icon,
  ArrowUpDownIcon,
  HelpCircleIcon,
  Delete01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { applicationDomainScopeSwitchId } from '@terra/shared-utils';
import { Field } from '../../components/field';
import { IconButton } from '../../components/icon-button';
import { createSelectListCollection, Select } from '../../components/select';
import { Tooltip } from '../../components/tooltip';
import { MetaDefault } from '../../components/typography';
import { DomainScopeSwitch } from './DomainScopeSwitch';
import { PATH_SCOPE_KINDS, type PathScopeKind } from './domain-scope.utils';

const scopingOptionKeys = {
  allowlist: {
    label: 'addPathsDialog.allowlistOption',
    tooltip: 'addPathsDialog.allowlistTooltip',
  },
  blocklist: {
    label: 'addPathsDialog.blocklistOption',
    tooltip: 'addPathsDialog.blocklistTooltip',
  },
} as const satisfies Record<
  PathScopeKind,
  {
    label: string;
    tooltip: string;
  }
>;
const scopingOptions = PATH_SCOPE_KINDS.map((value) => ({ value }));
const scopingCollection = createSelectListCollection({
  items: scopingOptions.map(({ value }) => ({ label: value, value })),
});

export const ADD_PATH_DRAFT_GRID_TEMPLATE = 'minmax(0, 1fr) 160px auto';
export const AddPathDraftColumnHeaders = () => {
  const t = useTranslations('designSystem.domainScope');

  return (
    <Grid
      gap={2}
      alignItems="end"
      style={{ gridTemplateColumns: ADD_PATH_DRAFT_GRID_TEMPLATE }}
    >
      <GridItem minW={0}>
        <Field.Label fontSize="xs">
          <HStack gap={1} alignItems="center">
            <MetaDefault>{t('addPathsDialog.pathLabel')}</MetaDefault>
            <Tooltip title={t('addPathsDialog.pathTooltip')}>
              <HelpCircleIcon size={14} />
            </Tooltip>
          </HStack>
        </Field.Label>
      </GridItem>
      <GridItem minW={0}>
        <Field.Label fontSize="xs">
          {t('addPathsDialog.scopingLabel')}
        </Field.Label>
      </GridItem>
      <GridItem aria-hidden w={6} />
    </Grid>
  );
};

export type PathDraftRowState = {
  id: string;
  path: string;
  kind: PathScopeKind | null;
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty: boolean;
};
type AddPathDraftRowProps = {
  row: PathDraftRowState;
  domain: string;
  pathError?: string | null;
  canRemove: boolean;
  isPrimaryHost?: boolean;
  onPathChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onKindChange: (kind: PathScopeKind) => void;
  onFunctionalityChange: (enabled: boolean) => void;
  onTestingChange: (enabled: boolean) => void;
  onThirdPartyChange: (enabled: boolean) => void;
  onRemove: () => void;
};

export const AddPathDraftRow = ({
  row,
  domain,
  pathError,
  canRemove,
  isPrimaryHost = false,
  onPathChange,
  onKindChange,
  onFunctionalityChange,
  onTestingChange,
  onThirdPartyChange,
  onRemove,
}: AddPathDraftRowProps) => {
  const t = useTranslations('designSystem.domainScope');
  const tCommon = useTranslations('designSystem.common');
  const scopingLabel = row.kind
    ? t(scopingOptionKeys[row.kind].label)
    : undefined;
  const hasPathError = Boolean(pathError);
  const showScopeToggles = row.kind === 'allowlist';
  const functionalityDisabled = row.isInTestingScope;
  const switchPath = row.path.trim() || undefined;

  return (
    <VStack gap={2} alignItems="stretch">
      <Grid
        gap={2}
        alignItems="start"
        style={{ gridTemplateColumns: ADD_PATH_DRAFT_GRID_TEMPLATE }}
      >
        <GridItem minW={0}>
          <Field.Root invalid={hasPathError}>
            <Field.Input
              placeholder={t('addPathsDialog.pathPlaceholder')}
              value={row.path}
              onChange={onPathChange}
              size="sm"
              w="full"
            />
            {pathError ? (
              <Field.ErrorText fontSize="xs" color="fg.neutral.error" mt={1}>
                {pathError}
              </Field.ErrorText>
            ) : null}
          </Field.Root>
        </GridItem>
        <GridItem minW={0}>
          <Select.Root
            positioning={{ sameWidth: true }}
            size="sm"
            width="full"
            collection={scopingCollection}
            value={row.kind ? [row.kind] : []}
            onValueChange={({ value }) => {
              const nextKind = value[0];

              if (nextKind === 'allowlist' || nextKind === 'blocklist') {
                onKindChange(nextKind);
              }
            }}
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText
                  placeholder={t('addPathsDialog.selectScoping')}
                >
                  {scopingLabel}
                </Select.ValueText>
                <ArrowUpDownIcon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {scopingOptions.map((option) => (
                  <Select.Item key={option.value} item={option}>
                    <HStack gap={2}>
                      <Select.ItemText
                        color={
                          option.value === 'blocklist'
                            ? 'fg.error'
                            : 'fg.default'
                        }
                      >
                        {t(scopingOptionKeys[option.value].label)}
                      </Select.ItemText>
                      <Tooltip
                        title={t(scopingOptionKeys[option.value].tooltip)}
                      >
                        <HelpCircleIcon size={16} />
                      </Tooltip>
                    </HStack>
                    <Select.ItemIndicator>
                      <Tick01Icon size={14} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </GridItem>
        <GridItem
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="8"
        >
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={tCommon('delete')}
            disabled={!canRemove}
            onClick={onRemove}
          >
            <Delete01Icon size={14} />
          </IconButton>
        </GridItem>
      </Grid>

      {showScopeToggles ? (
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          px={3}
          py={2}
        >
          <HStack gap={6} alignItems="center" flexWrap="wrap">
            <HStack gap={2} alignItems="center">
              <MetaDefault color="fg.muted">
                {t('columns.thirdParty')}
              </MetaDefault>
              <Tooltip title={t('tooltips.thirdParty')}>
                <HelpCircleIcon size={14} />
              </Tooltip>
              <DomainScopeSwitch
                checked={isPrimaryHost ? false : row.isThirdParty}
                disabled={isPrimaryHost}
                tooltip={
                  isPrimaryHost || !row.isThirdParty
                    ? t('disabled')
                    : t('enabled')
                }
                dataTestid={applicationDomainScopeSwitchId({
                  field: 'thirdParty',
                  host: domain,
                  path: switchPath,
                })}
                onCheckedChange={() => {
                  if (isPrimaryHost) {
                    return;
                  }

                  onThirdPartyChange(!row.isThirdParty);
                }}
              />
            </HStack>
            <HStack gap={2} alignItems="center">
              <MetaDefault color="fg.muted">
                {t('columns.functionality')}
              </MetaDefault>
              <Tooltip title={t('tooltips.functionality')}>
                <HelpCircleIcon size={14} />
              </Tooltip>
              <DomainScopeSwitch
                checked={row.isInFunctionalityScope}
                disabled={functionalityDisabled}
                tooltip={
                  row.isInFunctionalityScope ? t('enabled') : t('disabled')
                }
                dataTestid={applicationDomainScopeSwitchId({
                  field: 'interaction',
                  host: domain,
                  path: switchPath,
                })}
                onCheckedChange={() =>
                  onFunctionalityChange(!row.isInFunctionalityScope)
                }
              />
            </HStack>
            <HStack gap={2} alignItems="center">
              <MetaDefault color="fg.muted">{t('columns.testing')}</MetaDefault>
              <Tooltip title={t('tooltips.testing')}>
                <HelpCircleIcon size={14} />
              </Tooltip>
              <DomainScopeSwitch
                checked={row.isInTestingScope}
                tooltip={row.isInTestingScope ? t('enabled') : t('disabled')}
                dataTestid={applicationDomainScopeSwitchId({
                  field: 'attack',
                  host: domain,
                  path: switchPath,
                })}
                onCheckedChange={() => onTestingChange(!row.isInTestingScope)}
              />
            </HStack>
          </HStack>
        </Box>
      ) : null}
    </VStack>
  );
};
